import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import styles from './TopStories.module.css';
import { useFeed, Item, useRevolveAd } from './TopStories.hooks';
import { Loading } from '../../../components/loading/Loading';
import config from '../../../config';
import { DateTime } from 'luxon';

const FEED_URL = 'https://www.pw.org/articles/feed';

const DATE_FORMAT = 'MMMM dd, yyyy';

export const TopStories = () => {
	const [feedUrl] = useState<string>(FEED_URL);
	const [searchParams, setSearchParams] = useSearchParams();
	const { feed, isLoading } = useFeed(feedUrl);
	const { title } = config.build;

	useEffect(() => {
		const updatedSearchParams = new URLSearchParams(
			searchParams.toString(),
		);
		updatedSearchParams.set('section', 'top-stories');
		setSearchParams(updatedSearchParams.toString());
	}, []);
	const classNames = [styles['feed-container']];
	if (isLoading) {
		classNames.push(styles['loading']);
	}
	return (
		<section className={styles['top-stories']}>
			<div className={classNames.join(' ')}>
				{isLoading ? (
					<div className={styles['loading-container']}>
						<Loading />
					</div>
				) : (
					<>
						<h1>The Latest</h1>
						<h2>
							From the editors at <em>{title} Magazine</em>,{' '}
							<br />
							here’s the latest:
						</h2>
						<div className={styles['feed']}>
							{feed
								.map((item: Item, i: number) => (
									<FeedItem key={i} {...item} />
								))
								.reduce(reduceBanner, [])}
						</div>
						<footer>
							Discover more at{' '}
							<a
								href="https://www.pw.org"
								rel="noreferrer"
								target="_blank"
							>
								pw.org
							</a>
						</footer>
					</>
				)}
			</div>
		</section>
	);
};

function reduceBanner(acc: Array<any>, item: any, i: number): Array<any> {
	acc.push(item);
	if (i === 2) {
		acc.push(
			<Banner
				id="fbf717dc1969dc79e517b3994cd0df44"
				zone={65}
				key={`${i}-ad`}
				ad="ADVERTISEMENT"
			/>,
		);
	}
	if (i === 5) {
		acc.push(
			<Banner
				id="fbf717dc1969dc79e517b3994cd0df44"
				zone={64}
				key={`${i}-ad`}
				ad="ADVERTISEMENT"
			/>,
		);
	}

	return acc;
}

function Banner({ id, zone, ad }: { id: string; zone: number; ad?: string }) {
	const ref = useRef<any>();
	useRevolveAd(ref);
	return (
		<div ref={ref} className={styles['banner']}>
			<div>
				<div className={styles['ins-wrapper']}>
					<ins data-revive-zoneid={zone} data-revive-id={id}></ins>
				</div>
				{ref && ad ? <div className={styles['ad']}>{ad}</div> : null}
			</div>
		</div>
	);
}

export const Stories = ({ stories }: StoriesProps) => {
	return (
		<div className={styles['stories']}>
			<div className={styles['feed-container']}>
				{stories.map((story) => (
					<Story key={story.id} {...story} />
				))}
			</div>
		</div>
	);
};

interface StoriesProps {
	stories: Array<StoryProps>;
}

const FeedItem = (item: Item) => {
	const { title, category, pubDate, thumbnail, description } = item;
	const navigate = useNavigate();
	const dateTime = DateTime.fromJSDate(pubDate);

	const onClick = () => {
		console.log(`Description: `, description);
		navigate(`/feed`, {
			replace: true,
			state: {
				title,
				section: 'top-stories',
				description,
				item,
			},
		});
	};

	const subject = [];

	subject.push(dateTime.toFormat(DATE_FORMAT));

	return (
		// eslint-disable-line jsx-a11y/no-static-element-interactions
		// eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
		<div className={styles['feed-item-container']} onClick={onClick}>
			<div className={styles['feed-item']}>
				<div className={styles['image']}>
					{thumbnail ? (
						<img src={thumbnail} alt={'Thumbnail'} />
					) : (
						<div />
					)}
				</div>
				<div className={styles['title']}>
					{category ? <h4>{category}</h4> : null}
					<h3>{title}</h3>
				</div>
				<div className={styles['content']}>
					<small>{subject.join(' - ')}</small>
				</div>
			</div>
		</div>
	);
};

export const Story = ({ title, thumbnail, tags, excerpt, url }: StoryProps) => {
	let tagsComponent: Array<any> | null = null;
	if (tags) {
		tagsComponent = tags.map((tag) => <div key={tag}>{tag}</div>);
	}
	return (
		<div className={styles['story']}>
			<a
				className={styles['image']}
				href={url}
				target="_blank"
				rel="noreferrer"
			>
				<img src={thumbnail} alt={`${title} - cover`} />
			</a>

			<h4>
				<a href={url} target="_blank" rel="noreferrer">
					{title}
				</a>
			</h4>
			{tagsComponent?.length ? (
				<div className={styles['tags']}>{tagsComponent}</div>
			) : null}

			{excerpt ? <p>{excerpt}</p> : null}
		</div>
	);
};

export const Offer = ({ title, thumbnail, url }: StoryProps) => {
	return (
		<div className={styles['offer']}>
			<a href={url} target="_blank" rel="noreferrer">
				<img src={thumbnail} alt={title} />
			</a>
		</div>
	);
};

interface StoryProps {
	id: string;
	title: string;
	thumbnail?: string;
	url?: string;
	tags?: Array<string>;
	excerpt?: string;
	isOffer?: boolean;
}
